<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal" @click.stop>
      <div class="modal-header">
        <h3>Ajouter un Utilisateur</h3>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <div class="form-row">
          <div class="form-group">
            <label for="prenom">Prénom *</label>
            <input
              id="prenom"
              v-model="form.prenom"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.prenom }"
              placeholder="Prénom"
              required
            />
            <div v-if="errors.prenom" class="form-error">
              {{ errors.prenom }}
            </div>
          </div>

          <div class="form-group">
            <label for="nom">Nom *</label>
            <input
              id="nom"
              v-model="form.nom"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.nom }"
              placeholder="Nom de famille"
              required
            />
            <div v-if="errors.nom" class="form-error">
              {{ errors.nom }}
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="email">Adresse email *</label>
          <input
            id="email"
            v-model="form.email"
            type="email"
            class="form-control"
            :class="{ 'is-invalid': errors.email }"
            placeholder="exemple@email.com"
            required
          />
          <div v-if="errors.email" class="form-error">
            {{ errors.email }}
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="motDePasse">Mot de passe *</label>
            <div class="password-input-wrapper">
              <input
                id="motDePasse"
                v-model="form.motDePasse"
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': errors.motDePasse }"
                placeholder="Mot de passe"
                required
              />
              <button
                type="button"
                class="password-toggle"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
            <div v-if="errors.motDePasse" class="form-error">
              {{ errors.motDePasse }}
            </div>
            <div class="form-help">
              Le mot de passe doit contenir au moins 6 caractères
            </div>
          </div>

          <div class="form-group">
            <label for="role">Rôle *</label>
            <div class="select-wrapper">
              <select
                id="role"
                v-model="form.role"
                class="form-control"
                :class="{ 'is-invalid': errors.role }"
                required
              >
                <option value="">Sélectionner un rôle</option>
                <option v-for="role in roles" :key="role.value" :value="role.value">
                  {{ role.label }}
                </option>
              </select>
            </div>
            <div v-if="errors.role" class="form-error">
              {{ errors.role }}
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="telephone">Téléphone</label>
          <input
            id="telephone"
            v-model="form.telephone"
            type="tel"
            class="form-control"
            :class="{ 'is-invalid': errors.telephone }"
            placeholder="+33 1 23 45 67 89"
          />
          <div v-if="errors.telephone" class="form-error">
            {{ errors.telephone }}
          </div>
        </div>

        <div class="form-group">
          <label for="adresse">Adresse</label>
          <textarea
            id="adresse"
            v-model="form.adresse"
            class="form-control"
            :class="{ 'is-invalid': errors.adresse }"
            placeholder="Adresse complète"
            rows="3"
          ></textarea>
          <div v-if="errors.adresse" class="form-error">
            {{ errors.adresse }}
          </div>
        </div>

        <!-- Role Description -->
        <div v-if="selectedRoleDescription" class="role-description">
          <div class="role-info">
            <i class="fas fa-info-circle"></i>
            <div>
              <strong>{{ selectedRole?.label }}</strong>
              <p>{{ selectedRoleDescription }}</p>
            </div>
          </div>
        </div>

        <div v-if="submitError" class="submit-error">
          <i class="fas fa-exclamation-triangle"></i>
          {{ submitError }}
        </div>
      </form>

      <div class="modal-footer">
        <button 
          type="button" 
          @click="closeModal" 
          class="btn btn-secondary"
        >
          Annuler
        </button>
        <button 
          @click="handleSubmit"
          :disabled="loading"
          class="btn btn-primary"
        >
          <span v-if="loading" class="loading-spinner"></span>
          <i v-else class="fas fa-user-plus"></i>
          {{ loading ? 'Création...' : 'Créer l\'utilisateur' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'AddUserModal',
  emits: ['close', 'user-added'],
  setup(props, { emit }) {
    const store = useStore()

    const loading = ref(false)
    const showPassword = ref(false)
    const submitError = ref('')

    const form = reactive({
      prenom: '',
      nom: '',
      email: '',
      motDePasse: '',
      role: '',
      telephone: '',
      adresse: ''
    })

    const errors = reactive({
      prenom: '',
      nom: '',
      email: '',
      motDePasse: '',
      role: '',
      telephone: '',
      adresse: ''
    })

    const roles = computed(() => store.getters['users/roles'])

    const selectedRole = computed(() => {
      return roles.value.find(role => role.value === form.role)
    })

    const selectedRoleDescription = computed(() => {
      return selectedRole.value?.description
    })

    const validateForm = () => {
      // Reset errors
      Object.keys(errors).forEach(key => {
        errors[key] = ''
      })

      let isValid = true

      // Prénom validation
      if (!form.prenom.trim()) {
        errors.prenom = 'Le prénom est requis'
        isValid = false
      } else if (form.prenom.trim().length < 2) {
        errors.prenom = 'Le prénom doit contenir au moins 2 caractères'
        isValid = false
      }

      // Nom validation
      if (!form.nom.trim()) {
        errors.nom = 'Le nom est requis'
        isValid = false
      } else if (form.nom.trim().length < 2) {
        errors.nom = 'Le nom doit contenir au moins 2 caractères'
        isValid = false
      }

      // Email validation
      if (!form.email.trim()) {
        errors.email = 'L\'email est requis'
        isValid = false
      } else if (!/\S+@\S+\.\S+/.test(form.email)) {
        errors.email = 'Format d\'email invalide'
        isValid = false
      }

      // Password validation
      if (!form.motDePasse) {
        errors.motDePasse = 'Le mot de passe est requis'
        isValid = false
      } else if (form.motDePasse.length < 6) {
        errors.motDePasse = 'Le mot de passe doit contenir au moins 6 caractères'
        isValid = false
      }

      // Role validation
      if (!form.role) {
        errors.role = 'Le rôle est requis'
        isValid = false
      }

      // Phone validation (optional)
      if (form.telephone && !/^[\+]?[0-9\s\-\(\)]{10,}$/.test(form.telephone)) {
        errors.telephone = 'Format de téléphone invalide'
        isValid = false
      }

      return isValid
    }

    const handleSubmit = async () => {
      submitError.value = ''

      if (!validateForm()) {
        return
      }

      loading.value = true

      try {
        await store.dispatch('users/createUser', {
          ...form,
          prenom: form.prenom.trim(),
          nom: form.nom.trim(),
          email: form.email.trim().toLowerCase()
        })

        emit('user-added')
      } catch (error) {
        submitError.value = error.message
      } finally {
        loading.value = false
      }
    }

    const closeModal = () => {
      emit('close')
    }

    return {
      form,
      errors,
      loading,
      showPassword,
      submitError,
      roles,
      selectedRole,
      selectedRoleDescription,
      handleSubmit,
      closeModal
    }
  }
}
</script>

<style scoped>
.modal {
  max-width: 600px;
  width: 100%;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-toggle {
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  color: var(--medium-gray);
  cursor: pointer;
  padding: 0.25rem;
  z-index: 1;
}

.password-toggle:hover {
  color: var(--dark-gray);
}

.role-description {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.05));
  border: 1px solid rgba(0, 70, 255, 0.2);
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 1rem;
}

.role-info {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.role-info i {
  color: var(--primary-blue);
  margin-top: 0.25rem;
}

.role-info strong {
  color: var(--primary-blue);
  display: block;
  margin-bottom: 0.25rem;
}

.role-info p {
  color: var(--medium-gray);
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.4;
}

.submit-error {
  background-color: rgba(239, 68, 68, 0.1);
  color: var(--error);
  padding: 0.75rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  margin-top: 1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .modal {
    margin: 1rem;
    max-width: none;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
